import { closingStatus } from '@libs/enums';
import { useLocalizedContents, useTry } from '@ps-smart-cp/ppfw-client';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { fetchCurrentProcessingMonth } from '../repositories';
import { calculateEndingYearMonth } from '../services';
import { useSpace } from './SpaceContext';

interface ProcessingMonthContextProps {
  processingMonth: Date;
  beginningYearMonth: Date;
  endingYearMonth: Date;
  carryOverState: string;
  processingMonthEom: Date;
  refetch: () => Promise<void>;
}

const ProcessingMonthContext = createContext<ProcessingMonthContextProps>({
  processingMonth: new Date(1900, 0, 1),
  beginningYearMonth: new Date(1900, 0, 1),
  endingYearMonth: new Date(1900, 0, 1),
  carryOverState: '',
  processingMonthEom: new Date(1900, 0, 1),
  refetch: async () => {},
});

function ProcessingMonthProvider({ children }: { children: ReactNode }) {
  const [processingMonth, setProcessingMonth] = useState<Date>(new Date(1900, 0, 1));
  const [beginningYearMonth, setBeginningYearMonth] = useState<Date>(new Date(1900, 0, 1));
  const [endingYearMonth, setEndingYearMonth] = useState<Date>(new Date(1900, 0, 1));
  const [carryOverState, setCarryOverState] = useState<string>('');
  const [processingMonthEom, setProcessingMonthEom] = useState<Date>(new Date(1900, 0, 1));
  const { locale } = useLocalizedContents();
  const { tryRequest } = useTry();
  const { resetAfterCarryOver } = useSpace();
  // 前回取得した処理月度。初回の取得では未設定（繰越とはみなさない）。
  const fetchedProcessingMonthRef = useRef<Date>(undefined);

  const refetch = useCallback(
    async () =>
      await tryRequest(async () => {
        const basicInfoData = await fetchCurrentProcessingMonth();
        // 処理月度が変わったら繰越が成功したとみなす（job の完了は通知されないため）。
        const previous = fetchedProcessingMonthRef.current;
        fetchedProcessingMonthRef.current = basicInfoData.processingMonth;
        if (previous && previous.getTime() !== basicInfoData.processingMonth.getTime()) {
          await resetAfterCarryOver();
        }
        setProcessingMonth(basicInfoData.processingMonth);
        setBeginningYearMonth(basicInfoData.beginningYearMonth);
        const newEndingYearMonth = calculateEndingYearMonth(basicInfoData.beginningYearMonth);
        setEndingYearMonth(newEndingYearMonth);
        setCarryOverState(closingStatus.toDisplay(basicInfoData.carryOverState, locale));
        setProcessingMonthEom(basicInfoData.processingMonthEom);
      }),
    [resetAfterCarryOver],
  );

  useEffect(() => {
    void refetch();
  }, []);

  return (
    <ProcessingMonthContext.Provider
      value={{
        processingMonth,
        beginningYearMonth,
        endingYearMonth,
        carryOverState,
        processingMonthEom,
        refetch,
      }}
    >
      {children}
    </ProcessingMonthContext.Provider>
  );
}

function useProcessingMonth() {
  return useContext(ProcessingMonthContext);
}

export { ProcessingMonthProvider, useProcessingMonth };
